'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, eachDayOfInterval, isSameDay, isWeekend } from 'date-fns';
import { Loader2, Calendar as CalendarIcon, Check, Trash2, Plus, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { apiClient } from '@/lib/api-client';
import { validateDateOrder } from '@/lib/validation';
import type { SchoolViewExtra, SchoolYear, Schedule, CoreSchedule, DayOfWeek } from '@/types/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQueryClient } from '@tanstack/react-query';
import { useSchoolYears } from '@/hooks/use-schools';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';

interface CalendarBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  school: SchoolViewExtra;
  schoolId: string;
  onSuccess: () => void;
}

interface FixedDate {
  date: string;
  schedule: string;
  special?: boolean; // Indicates if this is a vacation/special day
}

interface RotationData {
  date: string;
  schedule: string;
}

const weekDays = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
];

export function CalendarBuilderDialog({
  open,
  onOpenChange,
  school,
  schoolId,
  onSuccess,
}: CalendarBuilderDialogProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch school years to get fresh data - use schoolId to match the query key
  const { data: schoolYears } = useSchoolYears(schoolId);
  
  // Form state
  const [schoolYear, setSchoolYear] = useState<SchoolYear | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [rotationStart, setRotationStart] = useState<number>(0);
  const [lockedWeekdays, setLockedWeekdays] = useState<number[]>([]);
  const [fixedDates, setFixedDates] = useState<FixedDate[]>([]);
  const [rotationData, setRotationData] = useState<RotationData[]>([]);
  const [skipLogic, setSkipLogic] = useState<boolean>(true); // Skip rotation for special/vacation days

  const currentYear = schoolYears?.find(y => y.current);
  const rotationPattern = school.rotationPattern || [];
  const schoolPrimaryColor = school.primaryColor || '#1e40af';

  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };

  // Helper to parse date-only strings as local dates (avoid timezone issues)
  const parseLocalDate = (dateString: string): Date => {
    // Parse YYYY-MM-DD as local date by splitting and creating Date object
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Initialize with current school year
  useEffect(() => {
    if (currentYear && !schoolYear) {
      setSchoolYear(currentYear);
      if (currentYear.startDate) setStartDate(parseLocalDate(currentYear.startDate));
      if (currentYear.endDate) setEndDate(parseLocalDate(currentYear.endDate));
    }
  }, [currentYear, schoolYear]);

  // Generate rotation when we have all required data
  useEffect(() => {
    if (startDate && endDate && rotationPattern.length > 0 && step === 3) {
      const generated = generateRotation(
        startDate,
        endDate,
        rotationPattern,
        rotationStart,
        lockedWeekdays,
        fixedDates,
        skipLogic,
        school.schedules || []
      );
      setRotationData(generated);
    }
  }, [startDate, endDate, rotationPattern, rotationStart, lockedWeekdays, fixedDates, step, skipLogic, school.schedules]);

  const handleSubmit = async () => {
    if (!rotationData.length) {
      toast.error('No rotation data to submit');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Start and end dates are required');
      return;
    }

    const dateError = validateDateOrder(startDate, endDate);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    setIsSubmitting(true);
    try {
      // Check if a school year exists for this date range
      const existingYear = schoolYears?.find((year) => {
        if (!year.startDate || !year.endDate) return false;
        const yearStart = parseLocalDate(year.startDate);
        const yearEnd = parseLocalDate(year.endDate);
        return startDate >= yearStart && endDate <= yearEnd;
      });

      // Create school year if it doesn't exist
      if (!existingYear) {
        try {
          // Create CSV file for upload endpoint (endpoint only accepts CSV)
          // Format: school_id,start_date,end_date,name,emoji
          // name and emoji are required columns but can be empty for high schools
          const csvHeaders = 'school_id,start_date,end_date,name,emoji\n';
          const csvRow = `${school.schoolName},${format(startDate, 'yyyy-MM-dd')},${format(endDate, 'yyyy-MM-dd')},,\n`;
          const csvContent = csvHeaders + csvRow;
          
          const csvBlob = new Blob([csvContent], { type: 'text/csv' });
          const file = new File([csvBlob], 'school_year.csv', { type: 'text/csv' });
          
          const formData = new FormData();
          formData.append('file', file);
          
          await apiClient.post('/v2/admin/upload/school_years', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          // Invalidate and refetch school years queries to refresh the data - use schoolId to match query keys
          await queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'school_years'] });
          await queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
          // Explicitly refetch to ensure data is fresh
          await queryClient.refetchQueries({ queryKey: ['school', schoolId, 'school_years'] });
          
          toast.success('School year created');
        } catch (yearError: any) {
          console.warn('Could not create school year, but continuing with calendar build:', yearError);
          // Don't fail the entire operation - proceed with calendar build
        }
      }

      // Update the calendar
      await apiClient.put(`/v3/schedules/calendar/${school.schoolName}`, rotationData);
      toast.success('Calendar updated successfully');
      onSuccess();
      onOpenChange(false);
      // Reset
      setStep(1);
      setFixedDates([]);
      setRotationData([]);
    } catch (error: any) {
      console.error('Calendar update error:', error);
      toast.error(error.message || 'Failed to update calendar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return startDate && endDate && rotationStart !== undefined && !validateDateOrder(startDate, endDate);
    }
    if (step === 2) return true;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4 pb-4">
          <DialogTitle style={{ color: schoolPrimaryColor }} className="text-xl">
            Build Calendar
          </DialogTitle>
          
          {/* Step Labels */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={step === 1 ? 'font-medium text-foreground' : ''}>
              Basic Settings
            </span>
            <ChevronRight className="h-3 w-3" />
            <span className={step === 2 ? 'font-medium text-foreground' : ''}>
              Fixed Dates
            </span>
            <ChevronRight className="h-3 w-3" />
            <span className={step === 3 ? 'font-medium text-foreground' : ''}>
              Review
            </span>
          </div>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6">
            {/* School Year Selection */}
            <div className="space-y-2">
              <Label className="text-base font-medium">School Year</Label>
              <Select
                value={schoolYear?.id}
                onValueChange={(value) => {
                  const year = schoolYears?.find(y => y.id === value);
                  if (year) {
                    setSchoolYear(year);
                    if (year.startDate) setStartDate(parseLocalDate(year.startDate));
                    if (year.endDate) setEndDate(parseLocalDate(year.endDate));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a school year" />
                </SelectTrigger>
                <SelectContent>
                  {schoolYears && schoolYears.length > 0 ? (
                    schoolYears.map((year) => (
                      <SelectItem key={year.id} value={year.id}>
                        {year.startDate && year.endDate && (
                          <>
                            {parseLocalDate(year.startDate).getFullYear()} -{' '}
                            {parseLocalDate(year.endDate).getFullYear()}
                            {year.current && ' (Current)'}
                          </>
                        )}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="py-2 px-3 text-sm text-muted-foreground">
                      No school years available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label className="text-base font-medium">Date Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-normal text-muted-foreground">
                    Start Date
                  </Label>
                  <DatePicker
                    date={startDate}
                    onSelect={setStartDate}
                    disabled={(date) => isWeekend(date)}
                    placeholder="Select start date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-normal text-muted-foreground">
                    End Date
                  </Label>
                  <DatePicker
                    date={endDate}
                    onSelect={setEndDate}
                    disabled={(date) => isWeekend(date) || (startDate ? date < startDate : false)}
                    minDate={startDate}
                    placeholder="Select end date"
                  />
                </div>
              </div>
            </div>

            {/* Rotation Start */}
            <div className="space-y-2">
              <Label className="text-base font-medium">Start of Rotation</Label>
              <p className="text-sm text-muted-foreground">
                Select which day type the rotation should begin with
              </p>
              <Select
                value={rotationStart.toString()}
                onValueChange={(value) => setRotationStart(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select starting day type" />
                </SelectTrigger>
                <SelectContent>
                  {rotationPattern.map((dayType, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {dayType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Locked Weekdays */}
            <div className="space-y-3">
              <div>
                <Label className="text-base font-medium">Locked Days</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Select weekdays that should always use a specific day type (optional)
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {weekDays.map((day) => (
                  <Button
                    key={day.value}
                    variant={lockedWeekdays.includes(day.value) ? 'default' : 'outline'}
                    size="sm"
                    className={
                      lockedWeekdays.includes(day.value)
                        ? ''
                        : 'hover:bg-accent hover:text-accent-foreground'
                    }
                    style={
                      lockedWeekdays.includes(day.value)
                        ? {
                            backgroundColor: schoolPrimaryColor,
                            color: getContrastColor(schoolPrimaryColor),
                            borderColor: schoolPrimaryColor,
                          }
                        : {}
                    }
                    onClick={() => {
                      setLockedWeekdays((prev) =>
                        prev.includes(day.value)
                          ? prev.filter((d) => d !== day.value)
                          : [...prev, day.value]
                      );
                    }}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
              {lockedWeekdays.length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  No locked days selected. All weekdays will follow the rotation pattern.
                </p>
              )}
            </div>

            {/* Detected Locked Schedules */}
            {(() => {
              // Find all core schedules with dayOfWeek set
              const lockedSchedules = (school.schedules || []).filter(
                (s): s is CoreSchedule => s.variant === 'core' && !!(s as CoreSchedule).dayOfWeek
              );
              
              if (lockedSchedules.length > 0) {
                const dayOfWeekLabels: Record<string, string> = {
                  'M': 'Monday',
                  'T': 'Tuesday', 
                  'W': 'Wednesday',
                  'TH': 'Thursday',
                  'F': 'Friday',
                };
                
                return (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-base font-medium">🔒 Detected Locked Day Schedules</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        These schedules will automatically be used on their locked days
                      </p>
                    </div>
                    <div className="rounded-md border bg-blue-50/50 dark:bg-blue-950/20 p-3">
                      <div className="flex flex-wrap gap-2">
                        {lockedSchedules.map((s) => (
                          <Badge 
                            key={s.id} 
                            variant="outline"
                            className="bg-white dark:bg-gray-800 text-sm py-1"
                          >
                            <span className="font-medium">{s.displayName}</span>
                            <span className="text-muted-foreground ml-1">
                              ({s.dayType.name} → {dayOfWeekLabels[s.dayOfWeek || ''] || s.dayOfWeek})
                            </span>
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                        When the rotation indicates day type "{lockedSchedules.map(s => s.dayType.name).filter((v, i, a) => a.indexOf(v) === i).join('", "')}" on the corresponding weekday, the locked schedule will be used.
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Fixed Dates</h3>
              <p className="text-sm text-muted-foreground">
                Add vacation days, special schedules, or modifications that override the rotation pattern. This step is optional.
              </p>
            </div>
            
            {/* Add Fixed Date Form */}
            <FixedDateForm
              startDate={startDate}
              endDate={endDate}
              rotationPattern={rotationPattern}
              schoolPrimaryColor={schoolPrimaryColor}
              getContrastColor={getContrastColor}
              onAdd={(fixedDate) => {
                setFixedDates((prev) => {
                  // Remove any existing entry for this date, then add the new one
                  const filtered = prev.filter((fd) => fd.date !== fixedDate.date);
                  return [...filtered, fixedDate].sort((a, b) => 
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                  );
                });
              }}
            />
            
            {/* Fixed Dates Table */}
            {fixedDates.length > 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">
                        Added Fixed Dates ({fixedDates.length})
                      </Label>
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="p-3 text-left font-medium">Date</th>
                          <th className="p-3 text-left font-medium">Day</th>
                          <th className="p-3 text-left font-medium">Schedule</th>
                          <th className="p-3 text-left w-16">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fixedDates.map((fixedDate, index) => (
                          <tr
                            key={index}
                            className="border-t hover:bg-muted/50 transition-colors"
                          >
                            <td className="p-3 font-medium">
                              {format(new Date(fixedDate.date), 'MM/dd/yyyy')}
                            </td>
                            <td className="p-3 text-muted-foreground">
                              {format(new Date(fixedDate.date), 'EEEE')}
                            </td>
                            <td className="p-3">
                              <Badge
                                variant={
                                  fixedDate.schedule === 'X' || fixedDate.special
                                    ? 'secondary'
                                    : 'default'
                                }
                              >
                                {fixedDate.schedule === 'X' ? 'No School' : fixedDate.schedule}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => {
                                  setFixedDates((prev) => prev.filter((_, i) => i !== index));
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">
                    No fixed dates added yet. You can skip this step or add dates that override the rotation pattern.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Review Generated Calendar</h3>
              <p className="text-sm text-muted-foreground">
                Review the calendar entries that will be created. You can adjust the skip logic setting below.
              </p>
            </div>
            
            {/* Skip Logic Toggle */}
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5 flex-1">
                    <Label htmlFor="skip-logic" className="text-base font-medium">
                      Skip Rotation Logic
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, special schedules and vacation days won't advance the rotation counter. When disabled, they will count as a rotation day.
                    </p>
                  </div>
                  <Switch
                    id="skip-logic"
                    checked={skipLogic}
                    onCheckedChange={setSkipLogic}
                  />
                </div>
              </CardContent>
            </Card>
            
            {rotationData.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    {rotationData.length} day{rotationData.length !== 1 ? 's' : ''} will be added to the calendar
                  </p>
                </div>
                
                <Card>
                  <CardContent className="p-0">
                    <div className="max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted sticky top-0 z-10 border-b">
                          <tr>
                            <th className="p-3 text-left font-medium">Date</th>
                            <th className="p-3 text-left font-medium">Day</th>
                            <th className="p-3 text-left font-medium">Current</th>
                            <th className="p-3 text-left font-medium">New</th>
                            <th className="p-3 text-left font-medium">Schedule Used</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rotationData.map((item, index) => {
                            // Find the current schedule for this date
                            const currentSchedule = (school.calendar as any)?.mapping?.find(
                              (c: any) => c.dateNow === item.date
                            )?.scheduleDisplay;
                            
                            const itemDate = new Date(item.date);
                            
                            // Parse the schedule string to determine if it's locked or modified
                            // Format: "A" (regular), "A!T" (locked to Tuesday), "A!RALLY" (modified)
                            const dayOfWeekCodes = ['M', 'T', 'W', 'TH', 'F'];
                            const isLockedFormat = item.schedule.includes('!') && 
                              dayOfWeekCodes.includes(item.schedule.split('!')[1]);
                            const isModifiedFormat = item.schedule.includes('!') && !isLockedFormat;
                            
                            let scheduleUsed = '';
                            let isLocked = false;
                            
                            if (item.schedule === 'X') {
                              scheduleUsed = 'No School';
                            } else if (isLockedFormat) {
                              // Locked schedule format: "B!T" -> find "B - Tuesday" schedule
                              const [dayType, dayCode] = item.schedule.split('!');
                              const lockedSchedule = school.schedules?.find(s => 
                                s.variant === 'core' && 
                                s.dayType.name === dayType &&
                                (s as CoreSchedule).dayOfWeek === dayCode
                              );
                              scheduleUsed = lockedSchedule?.displayName || `${dayType} - ${dayCode}`;
                              isLocked = true;
                            } else if (isModifiedFormat) {
                              // Modified schedule format: "A!RALLY"
                              const [dayType, mod] = item.schedule.split('!');
                              scheduleUsed = `${dayType} - ${mod}`;
                            } else if (item.schedule.includes('-') || item.schedule.includes('#')) {
                              // Special/vacation format
                              scheduleUsed = item.schedule;
                            } else {
                              // Regular day type
                              const regularSchedule = school.schedules?.find(s =>
                                s.variant === 'core' &&
                                s.dayType.name === item.schedule &&
                                !(s as CoreSchedule).dayOfWeek
                              );
                              scheduleUsed = regularSchedule?.displayName || item.schedule;
                            }
                            
                            return (
                              <tr
                                key={index}
                                className="border-t hover:bg-muted/50 transition-colors"
                              >
                                <td className="p-3 font-medium">
                                  {format(itemDate, 'MM/dd/yyyy')}
                                </td>
                                <td className="p-3 text-muted-foreground">
                                  {format(itemDate, 'EEEE')}
                                </td>
                                <td className="p-3">
                                  {currentSchedule ? (
                                    <Badge variant="outline">
                                      {currentSchedule === 'X' ? 'No School' : currentSchedule}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground text-xs">—</span>
                                  )}
                                </td>
                                <td className="p-3">
                                  <Badge variant={item.schedule === 'X' ? 'secondary' : 'default'}>
                                    {item.schedule === 'X' ? 'No School' : item.schedule}
                                  </Badge>
                                </td>
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <span className={isLocked ? 'font-medium' : 'text-muted-foreground'}>
                                      {scheduleUsed}
                                    </span>
                                    {isLocked && (
                                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                        Locked
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-3 animate-spin opacity-50" />
                  <p className="text-sm text-muted-foreground">
                    Generating calendar preview...
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 mt-6 border-t">
          <div>
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="min-w-[100px]"
              >
                Previous
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="min-w-[100px]"
                style={{
                  backgroundColor: schoolPrimaryColor,
                  color: getContrastColor(schoolPrimaryColor),
                }}
              >
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !rotationData.length}
                className="min-w-[140px]"
                style={{
                  backgroundColor: schoolPrimaryColor,
                  color: getContrastColor(schoolPrimaryColor),
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Build Calendar
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Map JS day of week (0-6, 0=Sunday) to DayOfWeek code for calendar format
// This matches the old dashboard's weekdays array: ['S', 'M', 'T', 'W', 'TH', 'F']
function jsDayToDayOfWeek(jsDay: number): DayOfWeek | null {
  const mapping: Record<number, DayOfWeek> = {
    1: 'M',   // Monday
    2: 'T',   // Tuesday
    3: 'W',   // Wednesday
    4: 'TH',  // Thursday
    5: 'F',   // Friday
  };
  return mapping[jsDay] || null;
}

// Generate rotation pattern
// When a locked day is selected, the schedule format is: ${dayType}!${dayOfWeek}
// Example: "B!T" means B day on Tuesday, which uses the "B - Tuesday" locked schedule
function generateRotation(
  startDate: Date,
  endDate: Date,
  pattern: string[],
  rotationStart: number,
  lockedDays: number[],
  excludedDates: FixedDate[],
  skipLogic: boolean = true,
  schedules: Schedule[] = []
): RotationData[] {
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const rotation: RotationData[] = [];
  let currentDayIndex = rotationStart;

  // Filter to only weekdays
  const weekdays = days.filter(day => {
    const dayOfWeek = day.getDay();
    return dayOfWeek !== 0 && dayOfWeek !== 6; // Not Sunday or Saturday
  });

  for (const day of weekdays) {
    const dateString = format(day, 'yyyy-MM-dd');
    const jsDayOfWeek = day.getDay(); // 0=Sunday, 1=Monday, etc.
    const dayOfWeekCode = jsDayToDayOfWeek(jsDayOfWeek);
    
    // Check if this date has a fixed schedule
    const fixedDate = excludedDates.find(fd => 
      isSameDay(new Date(fd.date), day)
    );

    if (fixedDate) {
      // Check if it's a special/vacation day or no school day
      const isSpecialOrNoSchool = fixedDate.special || fixedDate.schedule === 'X';
      
      if (isSpecialOrNoSchool) {
        // For special days and no school, just use the fixed schedule
        rotation.push({
          date: dateString,
          schedule: fixedDate.schedule,
        });
        // Only skip rotation increment if skipLogic is enabled
        if (!skipLogic) {
          currentDayIndex++;
        }
      } else {
        // For modified schedules, prepend the day type: dayType!modification
        const nextDayType = pattern[currentDayIndex % pattern.length];
        rotation.push({
          date: dateString,
          schedule: `${nextDayType}!${fixedDate.schedule}`,
        });
        currentDayIndex++;
      }
    } else {
      // Get the day type from the rotation pattern
      const nextDayType = pattern[currentDayIndex % pattern.length];
      
      // Check if this weekday is in lockedDays
      // lockedDays contains JS day numbers (1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri)
      // When a day is locked, format is: dayType!dayOfWeekCode (e.g., "B!T" for B on Tuesday)
      const shouldLock = lockedDays && lockedDays.includes(jsDayOfWeek) && dayOfWeekCode;
      
      rotation.push({
        date: dateString,
        schedule: shouldLock ? `${nextDayType}!${dayOfWeekCode}` : nextDayType,
      });
      currentDayIndex++;
    }
  }

  return rotation;
}

// Fixed Date Form Component
interface FixedDateFormProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  rotationPattern: string[];
  schoolPrimaryColor: string;
  getContrastColor: (hexColor: string | undefined) => string;
  onAdd: (fixedDate: FixedDate) => void;
}

function FixedDateForm({
  startDate,
  endDate,
  rotationPattern,
  schoolPrimaryColor,
  getContrastColor,
  onAdd,
}: FixedDateFormProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(startDate);
  const [scheduleType, setScheduleType] = useState<string>('modified');
  const [dayType, setDayType] = useState<string>(rotationPattern[0] || '');
  const [modification, setModification] = useState<string>('');
  const [vacationType, setVacationType] = useState<string>('');
  const [customVacation, setCustomVacation] = useState<string>('');

  const vacationTypes = [
    { value: 'WINTER-BREAK', label: 'Winter Break' },
    { value: 'SPRING-BREAK', label: 'Spring Break' },
    { value: 'SUMMER-BREAK', label: 'Summer Break' },
    { value: 'THANKSGIVING', label: 'Thanksgiving' },
    { value: 'HOLIDAY', label: 'Holiday' },
    { value: 'custom', label: 'Custom...' },
  ];

  const handleAdd = () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    let schedule = '';
    let special = false;

    switch (scheduleType) {
      case 'modified':
        if (!dayType || !modification) {
          toast.error('Please select a day type and modification');
          return;
        }
        schedule = `${dayType}!${modification.toUpperCase().replace(/\s+/g, '-')}`;
        break;
      case 'vacation':
        special = true;
        if (vacationType === 'custom') {
          if (!customVacation) {
            toast.error('Please enter a vacation name');
            return;
          }
          schedule = customVacation.toUpperCase().replace(/\s+/g, '-');
        } else {
          if (!vacationType) {
            toast.error('Please select a vacation type');
            return;
          }
          schedule = vacationType;
        }
        break;
      case 'special':
        special = true;
        schedule = `#-${format(selectedDate, 'MM-dd-yyyy')}`;
        break;
      case 'no-school':
        schedule = 'X';
        special = true;
        break;
    }

    onAdd({
      date: format(selectedDate, 'yyyy-MM-dd'),
      schedule,
      special,
    });

    // Reset form
    setModification('');
    setCustomVacation('');
    toast.success('Fixed date added');
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="space-y-1 mb-4">
          <Label className="text-base font-medium">Add Fixed Date</Label>
          <p className="text-sm text-muted-foreground">
            Configure a specific date that overrides the rotation pattern
          </p>
        </div>
        
        {/* First row: Date and Type - always visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Date</Label>
            <DatePicker
              date={selectedDate}
              onSelect={setSelectedDate}
              disabled={(date) => {
                if (isWeekend(date)) return true;
                if (startDate && date < startDate) return true;
                if (endDate && date > endDate) return true;
                return false;
              }}
              minDate={startDate}
              placeholder="Select date"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Type</Label>
            <Select value={scheduleType} onValueChange={setScheduleType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="modified">Modified Schedule</SelectItem>
                <SelectItem value="vacation">Vacation</SelectItem>
                <SelectItem value="special">Special Day</SelectItem>
                <SelectItem value="no-school">No School</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Second row: Conditional fields based on type */}
        {scheduleType === 'modified' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Day Type</Label>
              <Select value={dayType} onValueChange={setDayType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select day type" />
                </SelectTrigger>
                <SelectContent>
                  {rotationPattern.map((type, index) => (
                    <SelectItem key={index} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Modification</Label>
              <Input
                value={modification}
                onChange={(e) => setModification(e.target.value)}
                placeholder="e.g., RALLY, ASSEMBLY"
              />
            </div>
          </div>
        )}

        {scheduleType === 'vacation' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Vacation Type</Label>
              <Select value={vacationType} onValueChange={setVacationType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vacation type" />
                </SelectTrigger>
                <SelectContent>
                  {vacationTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {vacationType === 'custom' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Custom Vacation Name</Label>
                <Input
                  value={customVacation}
                  onChange={(e) => setCustomVacation(e.target.value)}
                  placeholder="e.g., THANKSGIVING"
                />
              </div>
            )}
          </div>
        )}

        {scheduleType === 'special' && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Schedule Name</Label>
            <Input
              value={selectedDate ? `#-${format(selectedDate, 'MM-dd-yyyy')}` : ''}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Special schedules are automatically named based on the selected date
            </p>
          </div>
        )}

        {scheduleType === 'no-school' && (
          <div className="rounded-md bg-muted/50 p-3">
            <p className="text-sm text-muted-foreground">
              This date will be marked as "No School" and will skip the rotation counter if skip logic is enabled.
            </p>
          </div>
        )}

        <div className="pt-2 border-t">
          <Button
            onClick={handleAdd}
            className="w-full md:w-auto"
            style={{
              backgroundColor: schoolPrimaryColor,
              color: getContrastColor(schoolPrimaryColor),
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Fixed Date
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

